// Large blog contents are now loaded dynamically on-demand in the UI
// to achieve a 90-100 speed performance score on both desktop and mobile modes.

import { formattedBlog1Content } from "./formatted_blog_1";
import { formattedBlog2Content } from "./formatted_blog_2";
import { formattedBlog3Content } from "./formatted_blog_3";
import { formattedBlog4Content } from "./formatted_blog_4";
import { formattedBlog5Content } from "./formatted_blog_5";
import { formattedBlog6Content } from "./formatted_blog_6";
import { formattedBlog7Content } from "./formatted_blog_7";
import { formattedBlog8Content } from "./formatted_blog_8";
import { formattedBlog9Content } from "./formatted_blog_9";
import { formattedBlog10Content } from "./formatted_blog_10";
import { formattedBlog11Content } from "./formatted_blog_11";
import { formattedBlog12Content } from "./formatted_blog_12";
import { formattedBlog13Content } from "./formatted_blog_13";
import { formattedBlog14Content } from "./formatted_blog_14";
import { formattedBlog15Content } from "./formatted_blog_15";
import { formattedBlog16Content } from "./formatted_blog_16";
import { formattedBlog17Content } from "./formatted_blog_17";
import { formattedBlog18Content } from "./formatted_blog_18";
import { formattedBlog19Content } from "./formatted_blog_19";
import { formattedBlog20Content } from "./formatted_blog_20";
import { formattedBlog21Content } from "./formatted_blog_21";
import { formattedBlog22Content } from "./formatted_blog_22";
import { formattedBlog23Content } from "./formatted_blog_23";
import { formattedBlog24Content } from "./formatted_blog_24";
import { formattedBlog25Content } from "./formatted_blog_25";
import { formattedBlog26Content } from "./formatted_blog_26";
import { formattedBlog27Content } from "./formatted_blog_27";
import { formattedBlog28Content } from "./formatted_blog_28";
import { formattedBlog29Content } from "./formatted_blog_29";
import { formattedBlog30Content } from "./formatted_blog_30";

export interface FallbackBlogArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  publish_date: string;
  reading_time: string;
  summary: string;
  author: {
    name: string;
    avatar: string;
    bio: string;
    role: string;
    articles_count: number;
  };
  faqs: { question: string; answer: string; }[];
  content: string;
}

const defaultAuthor = {
  name: "Bishal Khanal",
  avatar: "/assets/bishal_khanal.png",
  bio: "Founder of plantidentifer.com and dedicated Science Biology Student bridging research with modern machine learning.",
  role: "Founder & Lead Botanist",
  articles_count: 17
};

export const PUBLISHED_BLOGS_FALLBACK: FallbackBlogArticle[] = [
  {
    id: "blog_1",
    slug: "how-to-identify-plants-ai",
    title: "The Complete Guide to Plant Identification for Beginners",
    category: "Plant Identification",
    publish_date: "June 6, 2026",
    reading_time: "5 min read",
    summary: "Unlock the mysteries of your garden. Learn how to diagnose unknown species, understand leaf structural venations, decode root conditions, and utilize modern automated neural-network computer vision directly from your smartphone.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is the single most reliable leaf visual feature when performing manual plant identification?",
        answer: "The single most reliable leaf visual feature is the leaf arrangement pattern along the stem, classified as alternate, opposite, or whorled. Rather than relying on leaf color, which fluctuates wildly under different nutrient levels, temperature changes, or light exposure, the physical node junction is genetically hardwired and remains perfectly identical across a species’ lifespan."
      },
      {
        question: "How does computer vision technology identify a leaf instantly compared to typical human observation?",
        answer: "Neural computer vision networks analyze plant leaves by processing high-dimensional input arrays. These systems scan leaf margins, serrations, petiole ratios, and secondary vascular venation routing maps concurrently. While humans observe colors and basic shapes, the AI calculates mathematical descriptors, comparing them against millions of pre-cataloged botanical specimens to determine the most statistically probable species in under two seconds."
      }
    ],
    content: formattedBlog1Content
  },
  {
    id: "blog_1782466935932",
    slug: "the-complete-beginners-guide-to-indoor-plant-identification",
    title: "The Complete Beginner's Guide to Indoor Plant Identification",
    category: "Indoor Plants",
    publish_date: "June 8, 2026",
    reading_time: "7 min read",
    summary: "Learn how to classify indoor houseplant specimens by leaf shapes, patterns, textures, and watering requirements.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Which houseplants are best for low-light conditions?",
        answer: "Resilient varieties such as Snake Plants (Sansevieria) and ZZ Plants (Zamioculcas zamiifolia) adapt well to low light, utilizing high chlorophyll density to convert minor ambient rays."
      }
    ],
    content: formattedBlog2Content
  },
  {
    id: "blog_1782468765251",
    slug: "why-plant-leaves-turn-yellow-and-how-to-fix-them",
    title: "Why Plant Leaves Turn Yellow and How to Fix Them",
    category: "Plant Diseases",
    publish_date: "June 10, 2026",
    reading_time: "6 min read",
    summary: "Demystify chlorosis and leaf yellowing. Learn how to identify water vs. nutrient deficiencies using visual checks.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How can I distinguish overwatering from underwatering?",
        answer: "Overwatered leaves feel limp and soft, whereas underwatered leaves turn crisp, dry, and brittle, with soil separating from the container edges."
      }
    ],
    content: formattedBlog3Content
  },
  {
    id: "blog_1782469701301",
    slug: "best-free-ai-plant-identifier-websites-for-accurate-recognition",
    title: "Best Free AI Plant Identifier Websites for Accurate Recognition",
    category: "Plant Identification",
    publish_date: "June 12, 2026",
    reading_time: "6 min read",
    summary: "Find the top web-based AI plant scanners that offer instant species matching and botanical insights without any app downloads.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Are web plant identifiers safe to use?",
        answer: "Yes, web-based tools are lightweight, run entirely in your browser window, and respect your privacy while providing instant plant diagnostics."
      }
    ],
    content: formattedBlog4Content
  },
  {
    id: "blog_1782470586224",
    slug: "how-to-identify-wild-plants-safely-before-touching-or-growing-them-at-home",
    title: "How to Identify Wild Plants Safely Before Touching or Growing Them at Home",
    category: "Gardening",
    publish_date: "June 14, 2026",
    reading_time: "7 min read",
    summary: "A complete safety guide to recognizing wild flowers, toxic weeds, and invasive species using your phone camera.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Is it safe to rely on AI for edible wild plants?",
        answer: "Always verify wild plant identifications with expert human botanists or verified books before consuming any plant material."
      }
    ],
    content: formattedBlog5Content
  },
  {
    id: "blog_1782471446222",
    slug: "plant-identification-by-leaf-flower-and-picture-ultimate-step-by-step-guide-for-beginners",
    title: "Plant Identification by Leaf, Flower, and Picture – Ultimate Step-by-Step Guide for Beginners",
    category: "Plant Identification",
    publish_date: "June 15, 2026",
    reading_time: "8 min read",
    summary: "Master the foundational rules of identifying flora using alternate leaf positions, petal anatomies, and high-accuracy AI photo matches.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is alternate leaf positioning?",
        answer: "It is a pattern where single leaves alternate sides along the stem, keeping node joins separated."
      }
    ],
    content: formattedBlog6Content
  },
  {
    id: "blog_1782477271391",
    slug: "10-most-common-plant-problems-and-how-ai-plant-identification-can-help-you-fix-them-fast",
    title: "10 Most Common Plant Problems and How AI Plant Identification Can Help You Fix Them Fast",
    category: "Pests & Problems",
    publish_date: "June 16, 2026",
    reading_time: "7 min read",
    summary: "Avoid major plant care failures. Learn to recognize early warnings of root rot, spider mites, and salt build-ups.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is root rot?",
        answer: "It is a severe fungal decay of roots caused by oversaturated soil lacking proper drainage and oxygen flow."
      }
    ],
    content: formattedBlog7Content
  },
  {
    id: "blog_1782478508155",
    slug: "how-to-use-a-plant-identifier-to-detect-plant-diseases-pests-and-nutrient-deficiencies-easily",
    title: "How to Use a Plant Identifier to Detect Plant Diseases, Pests, and Nutrient Deficiencies Easily",
    category: "Pests & Problems",
    publish_date: "June 18, 2026",
    reading_time: "7 min read",
    summary: "A step-by-step manual on taking macro photos to diagnose plant stress and fungal infections using neural vision models.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How can AI detect invisible pests?",
        answer: "AI recognizes microscopic visual indicators, such as webbing, stipples, or scale groupings, that escape casual glance."
      }
    ],
    content: formattedBlog8Content
  },
  {
    id: "blog_1782479558407",
    slug: "best-gardening-tips-for-beginners-after-identifying-plants-with-an-ai-plant-identifier-tool",
    title: "Best Gardening Tips for Beginners After Identifying Plants with an AI Plant Identifier Tool",
    category: "Gardening",
    publish_date: "June 19, 2026",
    reading_time: "8 min read",
    summary: "Transition from plant scanning to successful gardening. Learn proper soil prep, lighting schedules, and watering guidelines.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How can I avoid overwatering in a garden?",
        answer: "Always test soil dryness by inserting a finger two inches into the soil before adding moisture."
      }
    ],
    content: formattedBlog9Content
  },
  {
    id: "blog_1782551683320",
    slug: "indoor-vs-outdoor-plant-identification-guide-learn-the-difference-using-ai-and-photos-quickly",
    title: "Indoor vs Outdoor Plant Identification Guide – Learn the Difference Using AI and Photos Quickly",
    category: "Indoor Plants",
    publish_date: "June 20, 2026",
    reading_time: "7 min read",
    summary: "Compare the physiological adaptabilities of indoor tropical houseplants with sturdy outdoor landscape species.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Why can't most indoor plants survive outdoor winters?",
        answer: "Indoor plants are primarily native to tropical understories and lack physiological mechanisms to withstand freezing temperatures."
      }
    ],
    content: formattedBlog10Content
  },
  {
    id: "blog_1782552780006",
    slug: "how-to-identify-succulents-and-cactus-plants-correctly-using-ai-plant-identifier-tools",
    title: "How to Identify Succulents and Cactus Plants Correctly Using AI Plant Identifier Tools",
    category: "Succulents",
    publish_date: "June 21, 2026",
    reading_time: "6 min read",
    summary: "Identify desert cactus, jade, aloe, and delicate rosettes. Learn the visual traits that distinguish unique succulent varieties.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How often should succulents be watered?",
        answer: "Allow the soil to dry out completely, then water thoroughly (the 'soak and dry' method), typically every 2-3 weeks."
      }
    ],
    content: formattedBlog11Content
  },
  {
    id: "blog_1782553690237",
    slug: "best-ai-plant-identifier-apps-and-websites-for-accurate-plant-recognition",
    title: "Best AI Plant Identifier Apps and Websites for Accurate Plant Recognition",
    category: "Plant Identification",
    publish_date: "June 22, 2026",
    reading_time: "7 min read",
    summary: "We review and rank the top AI-powered botanical scanning applications based on species precision and care tools.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Which scanner is best for plant diseases?",
        answer: "Our web-based AI Plant Identifier features integrated disease recognition, identifying fungal and watering symptoms instantly."
      }
    ],
    content: formattedBlog12Content
  },
  {
    id: "blog_1782554830950",
    slug: "why-your-indoor-plants-keep-dying-and-how-ai-plant-identification-can-help",
    title: "Why Your Indoor Plants Keep Dying and How AI Plant Identification Can Help",
    category: "Indoor Plants",
    publish_date: "June 23, 2026",
    reading_time: "6 min read",
    summary: "Unlock the real reasons why your indoor plants wilt or wither, and how to use mobile AI tracking to correct care schedules.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is the primary reason houseplants die?",
        answer: "Overwatering and stagnant, poorly draining soil are the leading causes of indoor plant root rot and decay."
      }
    ],
    content: formattedBlog13Content
  },
  {
    id: "blog_1782555994112",
    slug: "easy-ways-to-identify-toxic-plants-around-your-home-and-garden-using-ai",
    title: "Easy Ways to Identify Toxic Plants Around Your Home and Garden Using AI",
    category: "Pests & Problems",
    publish_date: "June 24, 2026",
    reading_time: "7 min read",
    summary: "Keep children and pets safe. Learn to detect common toxic houseplants and dangerous outdoor ornamentals with AI.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Which common houseplants are toxic to cats?",
        answer: "Lilies, Pothos, Peace Lilies, and Monstera species contain calcium oxalate crystals and are toxic to domestic pets."
      }
    ],
    content: formattedBlog14Content
  },
  {
    id: "blog_1782565938931",
    slug: "how-to-identify-flowering-plants-instantly-with-ai-and-smartphone-photos",
    title: "How to Identify Flowering Plants Instantly with AI and Smartphone Photos",
    category: "Flowers",
    publish_date: "June 25, 2026",
    reading_time: "6 min read",
    summary: "Flowering plants add color and vibrant life to any garden. Learn how to instantly identify unknown flowers, capture proper macro photos of petals, and leverage automated computer vision for precise taxonomic matches.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Can the AI distinguish between rose cultivars?",
        answer: "Yes, the model processes flower petal layering, color gradients, and petal margin geometry to recognize unique cultivars."
      }
    ],
    content: formattedBlog15Content
  },
  {
    id: "blog_1782573832930",
    slug: "ai-plant-identifier-for-houseplants-complete-indoor-plant-care-guide-for-beginners",
    title: "AI Plant Identifier for Houseplants – Complete Indoor Plant Care Guide for Beginners",
    category: "Houseplants",
    publish_date: "June 26, 2026",
    reading_time: "8 min read",
    summary: "Discover the ultimate roadmap to starting your indoor plant collection. Learn how to choose the right beginner houseplants, schedule watering intelligently, and use AI scanners to instantly diagnose and care for your green family.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Which houseplants are best for clean air?",
        answer: "Peace Lilies, Spider Plants, and Snake Plants are highly efficient at filtering air while requiring minimal maintenance."
      }
    ],
    content: formattedBlog16Content
  },
  {
    id: "blog_1782574866186",
    slug: "common-signs-of-unhealthy-plants-and-how-ai-can-detect-problems-early",
    title: "Common Signs of Unhealthy Plants and How AI Can Detect Problems Early",
    category: "Plant Diseases",
    publish_date: "June 27, 2026",
    reading_time: "7 min read",
    summary: "Foliage yellowing, drooping stems, and dark leaf spots are clear indicators of plant stress. Learn how to decode these symptoms and use AI scanning to diagnose issues before they become fatal.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How can I diagnose leaf spot disease?",
        answer: "Leaf spot disease appears as small, concentric brown or black circles with yellow halos, indicating a fungal or bacterial infection."
      }
    ],
    content: formattedBlog17Content
  },
  {
    id: "blog_18",
    slug: "essential-soil-mixes-for-healthy-houseplants",
    title: "Essential Soil Mixes for Healthy Houseplants: The Complete Recipe",
    category: "Plant Care",
    publish_date: "June 28, 2026",
    reading_time: "15 min read",
    summary: "Master the science of houseplant soil. Discover 6 custom DIY potting recipes for Monsteras, Cacti, Ferns, Orchids, and Ficus, featuring perlite, orchid bark, coco coir, pumice, and activated charcoal.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Why is regular garden soil bad for indoor houseplants?",
        answer: "Garden soil is too heavy and dense for container plants. It compacts around roots, cutting off oxygen and causing the perched water table effect, which leads to fatal root rot."
      },
      {
        question: "What is the function of activated charcoal in houseplant soil?",
        answer: "Horticultural activated charcoal purifies soil by absorbing excess fertilizer salts, heavy metals, and sour odors while deterring fungus gnats and springtails."
      }
    ],
    content: formattedBlog18Content
  },
  {
    id: "blog_19",
    slug: "how-to-propagate-your-favorite-houseplants-step-by-step",
    title: "How to Propagate Your Favorite Houseplants: Step-by-Step Guide",
    category: "Plant Care",
    publish_date: "June 29, 2026",
    reading_time: "16 min read",
    summary: "Multiply your plant collection like a master botanist. Learn water propagation, sphagnum moss rooting, leaf cuttings, and root division, complete with species matrices and hormonal rooting tips.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How often should I change propagation water?",
        answer: "Change propagation water every 3 to 5 days to replenish dissolved oxygen and prevent bacterial or fungal buildup on fresh root tissue."
      },
      {
        question: "Why do cuttings need a stem node to produce roots?",
        answer: "Nodes contain dense meristematic tissues and natural auxins required to differentiate cells into adventitious roots. Without a node, stem cuttings cannot generate new root systems."
      }
    ],
    content: formattedBlog19Content
  },
  {
    id: "blog_20",
    slug: "best-low-light-indoor-plants-for-dark-apartments",
    title: "Best Low-Light Indoor Plants for Dark Apartments",
    category: "Indoor Plants",
    publish_date: "June 30, 2026",
    reading_time: "15 min read",
    summary: "No windows? No problem. Discover 12 shadow-tolerant houseplants, photobiology principles, Foot-Candle measurements, reduced watering rules, and grow light guidelines for dark apartments.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Can plants survive in rooms with absolutely no natural light?",
        answer: "Yes, low-light plants like ZZ Plants and Snake Plants can thrive in windowless rooms if full-spectrum LED grow lights run for 12 to 14 hours daily."
      },
      {
        question: "Why do variegated plants turn solid green in dark rooms?",
        answer: "Variegated white or yellow leaves lack chlorophyll. In low light, the plant produces more green chlorophyll to maximize photon capture, causing variegated leaves to revert to solid green."
      }
    ],
    content: formattedBlog20Content
  },
  {
    id: "blog_21",
    slug: "how-to-repot-bound-plants-without-damaging-roots",
    title: "How to Repot Root-Bound Plants Without Damaging Roots",
    category: "Plant Care",
    publish_date: "July 1, 2026",
    reading_time: "16 min read",
    summary: "An in-depth botanical masterclass on identifying root constriction, teasing apart circling root masses, vertical scoring techniques, pot upgrades, and post-transplant recovery care.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Should I water a plant immediately after repotting?",
        answer: "Yes, deep watering immediately after repotting settles fresh soil particles, eliminates hidden air pockets around root tips, and reduces transplant shock."
      },
      {
        question: "What is root scoring and when is it necessary?",
        answer: "Root scoring involves making shallow vertical cuts along impenetrable, dense root balls. This breaks apical root dominance and forces fresh lateral roots to grow outward into new soil."
      }
    ],
    content: formattedBlog21Content
  },
  {
    id: "blog_22",
    slug: "ultimate-guide-to-watering-houseplants-correctly",
    title: "The Ultimate Guide to Watering Houseplants Correctly",
    category: "Plant Care",
    publish_date: "July 2, 2026",
    reading_time: "15 min read",
    summary: "Master cellular plant hydration. Understand transpiration streams, top-watering vs. bottom-watering, tap water fluoride issues, diagnostic matrices, and seasonal watering formulas.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is the best time of day to water houseplants?",
        answer: "Early morning is optimal. It gives root cells water before afternoon heat and allows any stray leaf moisture to evaporate, preventing fungal leaf spots."
      },
      {
        question: "Why does my plant drop leaves right after I water it?",
        answer: "If a plant drops leaves right after watering, it likely suffers from severe root rot. Damaged roots cannot process water, causing immediate cellular hypoxia and leaf shed."
      }
    ],
    content: formattedBlog22Content
  },
  {
    id: "blog_23",
    slug: "safe-organic-pest-control-for-organic-gardening",
    title: "Safe Organic Pest Control for Organic Gardening",
    category: "Gardening",
    publish_date: "July 3, 2026",
    reading_time: "16 min read",
    summary: "Eliminate garden pests naturally. Comprehensive guide to Integrated Pest Management (IPM), emulsified cold-pressed Neem Oil, insecticidal soap, Bacillus thuringiensis, and predatory insects.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Does neem oil wash off edible herbs and vegetables?",
        answer: "Yes, neem oil is non-toxic to humans and washes off easily. Always rinse harvested crops thoroughly with fresh water before eating."
      },
      {
        question: "Why did my plant leaves turn brown after spraying neem oil?",
        answer: "Foliar burn occurs when oil sprays are applied during hot, sunny weather (above 85°F). Always apply neem oil during late evening so it dries overnight before intense sunlight hits."
      }
    ],
    content: formattedBlog23Content
  },
  {
    id: "blog_24",
    slug: "how-to-grow-fresh-herbs-indoors-all-year-round",
    title: "How to Grow Fresh Herbs Indoors All Year Round",
    category: "Herbs",
    publish_date: "July 4, 2026",
    reading_time: "15 min read",
    summary: "Cultivate a kitchen herb garden year-round. Master Mediterranean vs. soft herbs, soil recipes, indoor LED grow light setups, and stem pinching techniques that double harvest yields.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Which herb is the easiest to grow indoors for beginners?",
        answer: "Mint and Sweet Basil are extremely fast-growing and resilient, making them ideal starter herbs for indoor kitchen growers."
      },
      {
        question: "How does pinching stem tops increase herb yields?",
        answer: "Pinching off terminal growth tips destroys apical auxin dominance, triggering dormant lateral buds at leaf nodes to sprout into two new branches, doubling stem density."
      }
    ],
    content: formattedBlog24Content
  },
  {
    id: "blog_25",
    slug: "understanding-fertilizer-npk-ratios-for-your-garden",
    title: "Understanding Fertilizer NPK Ratios for Your Garden",
    category: "Gardening",
    publish_date: "July 5, 2026",
    reading_time: "16 min read",
    summary: "Deconstruct N-P-K fertilizer numbers. Learn how Nitrogen, Phosphorus, and Potassium drive chlorophyll synthesis, root/flower growth, and disease immunity across organic and synthetic options.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What happens if I over-fertilize my plants?",
        answer: "Over-fertilization causes 'fertilizer burn' due to high soil salt accumulation, which draws moisture out of root cells, turning leaf edges crisp and brown."
      },
      {
        question: "What NPK ratio is best for indoor foliage plants?",
        answer: "A high-nitrogen ratio like 3:1:2 (e.g., 24-8-16 or 9-3-6) is ideal for driving rich, green foliage and stem growth in tropical houseplants."
      }
    ],
    content: formattedBlog25Content
  },
  {
    id: "blog_26",
    slug: "how-to-raise-humidity-for-delicate-tropical-plants",
    title: "How to Raise Humidity for Delicate Tropical Plants",
    category: "Indoor Plants",
    publish_date: "July 6, 2026",
    reading_time: "15 min read",
    summary: "Stop crisp brown leaf margins forever. Understand Vapor Pressure Deficit (VPD), ultrasonic humidifiers, plant grouping microclimates, IKEA cabinet greenhouses, and the truth about misting.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Does misting leaves actually increase relative humidity?",
        answer: "No. Misting only raises relative humidity for 2 to 5 minutes until droplets evaporate, while standing leaf water encourages fungal leaf spot diseases."
      },
      {
        question: "What humidity level do Calatheas and Ferns require?",
        answer: "Delicate tropicals like Calatheas, Marantas, and Ferns require a relative humidity between 65% and 85% to prevent crisp leaf margin collapse."
      }
    ],
    content: formattedBlog26Content
  },
  {
    id: "blog_27",
    slug: "best-plants-to-grow-on-balcony-and-patios",
    title: "Best Plants to Grow on Balconies and Patios",
    category: "Gardening",
    publish_date: "July 7, 2026",
    reading_time: "15 min read",
    summary: "Transform urban outdoor spaces into lush sanctuaries. Evaluate balcony microclimates, structural weight limits, full-sun bloomers, shady retreat flora, dwarf edibles, and windbreak species.",
    author: defaultAuthor,
    faqs: [
      {
        question: "How often do balcony container plants need watering?",
        answer: "Outdoor balcony pots dry out fast due to sun and wind. In peak summer heat, they often require watering once daily or every other day."
      },
      {
        question: "How do I secure balcony containers against strong winds?",
        answer: "Anchor railing boxes with heavy-duty steel brackets. For tall plants like Bamboo or Olive trees, use wide pots and add pea gravel at the bottom as ballast."
      }
    ],
    content: formattedBlog27Content
  },
  {
    id: "blog_28",
    slug: "how-to-identify-and-grow-aromatic-plants",
    title: "How to Identify and Grow Aromatic Plants",
    category: "Herbs",
    publish_date: "July 8, 2026",
    reading_time: "15 min read",
    summary: "Explore the biochemistry of volatile organic compounds (VOCs). Learn field identification (square Lamiaceae stems), profiles of 10 legendary aromatic species, and morning harvest methods.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Can aromatic plants help repel garden pests naturally?",
        answer: "Yes, aromatic essential oils (like Linalool, Menthol, and Camphor) naturally deter mosquitoes, flies, cabbage moths, and garden pests."
      },
      {
        question: "When is the best time of day to harvest aromatic herbs?",
        answer: "Harvest early in the morning between 8 AM and 10 AM after dew dries, but before afternoon heat evaporates volatile essential oils from leaf surfaces."
      }
    ],
    content: formattedBlog28Content
  },
  {
    id: "blog_29",
    slug: "complete-guide-to-growing-medicinal-herbs-at-home",
    title: "Complete Guide to Growing Medicinal Herbs at Home",
    category: "Herbs",
    publish_date: "July 9, 2026",
    reading_time: "16 min read",
    summary: "Build your home botanical apothecary. Explore phytochemical active compounds (flavonoids, alkaloids, terpenes), profile 10 medicinal plants, master teas, tinctures, and salves, and learn safety rules.",
    author: defaultAuthor,
    faqs: [
      {
        question: "What is the difference between an herbal infusion and a decoction?",
        answer: "Infusions steep soft leaves and flowers in boiled water for 10-15 minutes. Decoctions simmer dense roots, bark, or rhizomes in boiling water for 20-30 minutes."
      },
      {
        question: "How long do alcohol tinctures remain potent?",
        answer: "High-proof alcohol tinctures stored in dark amber glass bottles remain biologically potent for 3 to 5 years."
      }
    ],
    content: formattedBlog29Content
  },
  {
    id: "blog_30",
    slug: "how-to-choose-the-right-pots-for-your-plants",
    title: "How to Choose the Right Pots for Your Plants",
    category: "Plant Care",
    publish_date: "July 10, 2026",
    reading_time: "5 min read",
    summary: "Terracotta, plastic, or ceramic? Discover how pot materials, drainage hole configurations, and size selections impact your plant's soil moisture levels.",
    author: defaultAuthor,
    faqs: [
      {
        question: "Do pots always need a drainage hole?",
        answer: "Yes, absolutely. Without drainage holes, excess water pools at the bottom of the pot, causing root rot and killing the plant."
      }
    ],
    content: formattedBlog30Content
  }
];
